/* eslint-disable react-hooks/exhaustive-deps */
import { FlexGrid } from "@grapecity/wijmo.react.grid";
import { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import FileHistoryButtonLayout from "../layout/FileHistoryButtonLayout";
import { Divider } from "@material-ui/core";
import Dialog from "../dialog/functional/Dialog";
import DialogContent from "../dialog/functional/DialogContent";
import DialogTitle from "../dialog/functional/DialogTitle";
import DialogHeaderWithClose from "../dialog/functional/DialogHeaderWithClose";
import AutoDataGridColumn from "../grid/functional/AutoDataGridColumn";
import apiClient from "../../lib/common/apiClient";
import getOption from "../../lib/api/getOptions";

/**
 * @COMPONENT_NAME FileHistoryButton
 * @DESCRIPTION props로 전달받은 grid, schema를 통해 현재 선택된 grid의 prikey의 값과 tableName을 쿼리로 담아
 * 				FileHistoryByPri로 api 호출을 보내 해당하는 파일이력 목록을 받아 dialog에 grid로 표시한다.
 *
 * @PROPS
 *   - {string} id - id 값
 *   - {object} grid - 사용중인 grid 데이터
 *   - {object} schema - 사용중인 table의 schema 데이터
 *   - {...any} others - FileHistoryButtonLayout, FlexGrid에 전달될 기타 props
 * @STATE
 * @state {boolean} open - Dialog open 여부
 * @state {array} items - API에서 가져온 파일 이력 데이터
 * @state {array} keys - schema로부터 추출된 primary key 컬럼 배열
 * @state {boolean} logHistory - 옵션값(logHistory)에 따른 버튼 표시 여부
 * 
 * @PASSTHROUGH_PROPS
 *   - {boolean} open - Dialog의 open 상태, 초기값은 false이며 버튼 클릭시 true로 변환하는 state
 *   - {function} onClick - 버튼 클릭 시 api호출 및 open state 상태를 true로 만드는 콜백함수
 *   - {function} onClose - open의 상태를 false로 만드는 콜백함수
 *   - {object} PaperProps - Dialog의 paper css값을 변경하는 객체 전달
 * @CALLBACKS 
 * @function makeSchemaColumns - 파일 이력 테이블에서 사용할 Grid column schema를 정의
 * @function fetchHistory - grid의 선택된 row에서 키값을 추출하고, 해당 값으로 API 호출 후 파일 이력 조회
 * @function openDialog - 버튼 클릭 시 Dialog open 및 파일 이력 조회 트리거
 * 
 * @EXAMPLES
 * ```jsx
 *  <FileHistoryButton 
		id={id}
		grid={grid}
		schema={schema}
		disabled={selectGrid ? false : true}
	/>
 * ```
 */

function FileHistoryButton(props) {
	const { t } = useTranslation();

	const { id, grid, schema, ...others } = props;
	const [items, setItems] = useState([]);
	const [keys, setKeys] = useState([]);
	const [open, setOpen] = useState(false);
	const [logHistory, setLogHistory] = useState(false);
	const fileHistoryTableName = "FileHistoryByPrikey";

	function makeSchemaColumns() {
		return [
			{
				name: "fileName",
				type: "varchar(255)",
				columnOrder: 10,
				grid: {
					width: 300,
				},
			},
			{
				name: "extension",
				type: "varchar(255)",
				columnOrder: 15,
				grid: {
					width: 100,
				},
			},
			{
				name: "fileLink",
				type: "varchar(255)",
				columnOrder: 20,
				grid: {
					type: "fileDownload",
					isReadOnly: true,
				},
			},
			{
				name: "operation",
				type: "varchar(255)",
				columnOrder: 25,
			},
			{
				name: "description",
				type: "varchar(255)",
				columnOrder: 30,
				grid: {
					type: "tooltip",
					width: 200,
				},
			},
			{
				name: "createdBy",
				type: "varchar(255)",
				columnOrder: 35,
			},
			{
				name: "createdAt",
				type: "varchar(255)",
				columnOrder: 40,
				grid: {
					width: 200,
				},
			},
		];
	}

	const schemaColumns = useMemo(() => makeSchemaColumns(), []);

	useEffect(() => {
		let mounted = true;

		(async () => {
			let logHistoryBoolean = await getOption("fileUpload", "logHistory");
			if (mounted) {
				if (logHistoryBoolean?.toLowerCase() === "true") {
					setLogHistory(true);
				} else {
					setLogHistory(false);
				}
			}
		})();

		return () => {
			mounted = false;
		};
	}, []);

	useEffect(() => {
		if (schema?.autoIncrement) {
			setKeys([schema.autoIncrement]);
		} else {
			setKeys(schema?.keys || []);
		}
	}, [schema, grid]);

	const fetchHistory = async () => {
		const selectedRow = grid.selection.row;
		if (selectedRow == null || selectedRow < 0) return;
		const rowData = grid.rows[selectedRow]?.dataItem;
		if (!rowData) return;
		// 키 추출
		const keyValueArray = keys.map((key) => rowData[key]);

		const keyQuery = keyValueArray
			.map((v) => `keys[]=${encodeURIComponent(v)}`)
			.join("&");
		const tableQuery = `&tableName=${schema?.name}`;
		try {
			const response = await apiClient.get(
				`/api/${fileHistoryTableName}?${keyQuery}${tableQuery}`
			);
			setItems(response.data.data);
		} catch (err) {
			console.error("파일 로그 조회 실패:", err);
		}
	};

	const openDialog = () => {
		setOpen(true);
		fetchHistory();
	};

	const groupedItems = useMemo(() => {
		const groupMap = {};
		for (const item of items) {
			const key = item.columnName || "기타";
			if (!groupMap[key]) groupMap[key] = [];
			groupMap[key].push(item);
		}
		return groupMap;
	}, [items]);
	return logHistory ? (
		<>
			<FileHistoryButtonLayout
				id={id}
				onClick={() => openDialog()}
				{...others}
			>
				{t("dialog.title.fileDownload.fileHistory")}
			</FileHistoryButtonLayout>
			<Dialog
				id={id}
				open={open}
				onClose={() => setOpen(false)}
				PaperProps={{
					style: { maxWidth: "1500px", width: "100%" },
				}}
			>
				<DialogHeaderWithClose>
					<DialogTitle>
						{t("dialog.title.fileDownload.fileHistory")}
					</DialogTitle>
				</DialogHeaderWithClose>

				<Divider />

				<DialogContent
					style={{
						padding: 0,
						width: "100%",
						height: "700px",
						overflowY: "auto",
					}}
				>     
					{items?.length > 0 ? (
						Object.keys(groupedItems).length <= 1 ? (
							<FlexGrid
								itemsSource={items}
								isReadOnly={true}
								{...others}
							>
								{schemaColumns.map((column) => (
									<AutoDataGridColumn
										key={column.name}
										schema={column}
									/>
								))}
							</FlexGrid>
						) : (
							Object.entries(groupedItems).sort(([a], [b]) => a.localeCompare(b)).map(
								([columnName, group]) => (
									<div
										key={columnName}
										style={{
											marginBottom: "40px",
											padding: "16px",
										}}
									>
										<div
											style={{
												fontSize: "18px",
												fontWeight: "bold",
												marginBottom: "8px",
											}}
										>
											{t("columnName." + columnName) ||
												columnName}
										</div>
										<FlexGrid
											itemsSource={group}
											isReadOnly={true}
											{...others}
										>
											{schemaColumns.map((column) => (
												<AutoDataGridColumn
													key={column.name}
													schema={column}
												/>
											))}
										</FlexGrid>
									</div>
								)
							)
						)
					) : (
						<div
							style={{
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
								height: "100%",
								border: "1px solid #ccc",
								borderRadius: "8px",
								backgroundColor: "#f9f9f9",
								fontSize: "22px",
								color: "#666",
								textAlign: "center",
								padding: "20px",
							}}
						>
							첨부파일 이력이 없습니다.
						</div>
					)}
				</DialogContent>
			</Dialog>
		</>
	) : (
		<></>
	);
}

export default FileHistoryButton;
